import { Button } from '@/components/common/Button';
import Divider from '@/components/common/Divider';
import PortalEntry from '@/components/common/PortalEntry';
import FormStepper from '@/components/form/components/FormStepper';
import { getDefaultCurrencyId } from '@/components/form/schema/common';
import {
    PortalFiatDepositFormInput,
    PortalFiatDepositFormValues,
    portalFiatDepositSchema
} from '@/components/form/schema/portalFiatDepositSchema';
import ErrorMessage from '@/components/inputs/ErrorMessage';
import FilterSelect from '@/components/inputs/FilterSelect';
import PortalInputController from '@/components/inputs/PortalInputController';
import PortalNumberInput from '@/components/inputs/PortalNumberInput';
import Select from '@/components/inputs/Select';
import TextArea from '@/components/inputs/TextArea';
import { acceptedFileTypes, DropzoneErrorMessage, maxFiles, maxFileSize } from '@/constants/general';
import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { ErrorMsg } from '@/types/common';
import useAbortController from '@/utils/hooks/useAbortController';
import useBankOptions from '@/utils/hooks/useBankOptions';
import useCurrencyOptions from '@/utils/hooks/useCurrencyOptions';
import useLanguage from '@/utils/hooks/useLanguage';
import { use401Error } from '@/utils/hooks/usePortalAuthentication';
import useToast from '@/utils/hooks/useToast';
import { useSso } from '@/utils/providers/SSOProvider';
import { Tab } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import axios from 'axios';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { MdClose, MdOutlineFileUpload, MdOutlineImage } from 'react-icons/md';
import { PortalDepositModalProps } from '.';
import { WhitelistError } from '../PortalInternalTransferModal';
import SSIProfileBankDetails from './SSIProfileBankDetails';

const defaultBankInfoId = { label: 'Select your whitelisted bank account', value: undefined };
const defaultCurrencyId = getDefaultCurrencyId('deposit', 'currency');

export const defaultValues: Partial<PortalFiatDepositFormInput> = {
    amount: undefined,
    currencyId: defaultCurrencyId,
    bankInfoId: defaultBankInfoId as any,
    requestDescription: '',
    supportingDetails: null
};

const FiatDepositForm = (props: PortalDepositModalProps) => {
    const { opened, mode, currency, handlers, onRefresh } = props;

    const lang = useLanguage();
    const [toastError, toastSuccess] = useToast();

    const [tabIndex, setTabIndex] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<ErrorMsg>({ message: '' });
    const [files, setFiles] = useState<FileWithPath[]>([]);

    const steps = useMemo(() => {
        return [
            { key: 1, label: lang.portal.requestDetails },
            { key: 2, label: lang.portal.supportDetails }
        ];
    }, [lang]);

    const form = useForm<PortalFiatDepositFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(portalFiatDepositSchema({}))
    });
    const {
        reset,
        watch,
        trigger,
        setValue,
        handleSubmit,
        formState: { isValid }
    } = form;

    const [selectedBankInfo, selectedCurrency] = watch(['bankInfoId', 'currencyId']);

    const { on401Error } = use401Error();
    const { jwt, foundEntity, ssiProfileBankInfoApi } = useSso();
    const { isCurrenciesLoading, fiatDepositCcyOptions } = useCurrencyOptions();
    const { bankOptions, bankDetails } = useBankOptions(selectedCurrency, selectedBankInfo);
    const { abortController, setAbortController, onClose } = useAbortController({
        isLoading,
        handlers
    });

    const { data: ssiProfileBankInfo } = ssiProfileBankInfoApi;

    const hasAccount = useMemo(() => {
        return selectedCurrency?.value && bankOptions?.length;
    }, [selectedCurrency, bankOptions]);

    const foundSSIProfile = useMemo(() => {
        if (ssiProfileBankInfo && ssiProfileBankInfo?.length > 0 && foundEntity) {
            const profile = ssiProfileBankInfo.find((profile) => profile.entityId === foundEntity.parentId);
            if (profile) {
                return profile.profileInfos.find(
                    (info) => info.baseCurrencyId === selectedCurrency?.value && info.active
                );
            }
        }
    }, [ssiProfileBankInfo, selectedCurrency, foundEntity]);

    const uploadFile = useCallback(
        async (requestId: number, entityId: number) => {
            if (files.length === 0) return false;

            const formData = new FormData();
            formData.append('file', files[0]);
            formData.append('entityId', entityId.toString());
            if (portalURL) {
                return await axios.postForm(portalAPI(`/api/requests/${requestId}/file-proof`), formData, {
                    headers: { Authorization: `Bearer ${jwt}` }
                });
            }
        },
        [files]
    );
    const onSubmit = async (data: PortalFiatDepositFormInput) => {
        setIsLoading(true);
        const dataValidated = data as PortalFiatDepositFormValues;
        try {
            if (foundEntity) {
                let depositResponse = {} as any;
                const payload = {
                    entityId: foundEntity.entityId,
                    amount: parseFloat(dataValidated.amount),
                    bankInfoId: dataValidated.bankInfoId.value,
                    requestDescription: dataValidated.requestDescription || null,
                    currencyId: dataValidated.currencyId.value
                };
                if (portalURL) {
                    depositResponse = await axios.post(portalAPI('/api/requests/deposit/fiat'), payload, {
                        headers: { Authorization: `Bearer ${jwt}` },
                        signal: abortController.signal
                    });
                }
                if (files.length && depositResponse?.data?.id)
                    await uploadFile(depositResponse.data.id, foundEntity.entityId);

                const title = 'Fiat Deposit';
                const body = 'Deposit request submitted successfully';
                toastSuccess({ body, title });
                onRefresh?.();
            }
            handlers.close();
        } catch (err: any) {
            on401Error(error, () => {
                logToServer(
                    'error',
                    `Unauthenticated access on /api/requests/deposit/fiat. JWT may be invalid or expired.`
                );
            });
            handleApiError(err);
        }
        setIsLoading(false);
    };

    const onError = (e: FieldErrors) => console.log(e);

    const handleApiError = useCallback((error) => {
        setIsLoading(false);
        setError(error?.response?.data || error);
    }, []);

    useEffect(() => {
        if (files.length) {
            setValue('supportingDetails', 'File Uploaded');
        } else {
            setValue('supportingDetails', null);
        }
        trigger('supportingDetails');
    }, [files]);

    useEffect(() => {
        if (bankOptions && bankOptions.length > 0 && bankOptions.length < 2) {
            setValue('bankInfoId', bankOptions[0], { shouldValidate: true });
        }
    }, [bankOptions]);

    useEffect(() => {
        if (selectedCurrency?.value) {
            setTabIndex(0);
            let refreshBankInfoId: any = defaultBankInfoId;
            if (bankOptions.length > 0) refreshBankInfoId = bankOptions[0];
            reset({
                ...defaultValues,
                bankInfoId: refreshBankInfoId,
                currencyId: selectedCurrency
            });
        }
    }, [selectedCurrency?.value]);

    useEffect(() => {
        if (selectedCurrency?.value) {
            if (!hasAccount || !bankOptions.length) {
                setError({ message: <WhitelistError type="FIAT" selectedCurrency={selectedCurrency} /> });
            } else {
                setError({ message: '' });
            }
        }
    }, [bankOptions, selectedCurrency, setValue]);

    useEffect(() => {
        const prefillCcy = fiatDepositCcyOptions.find((option) => option.name === currency);
        if (prefillCcy) setValue('currencyId', prefillCcy, { shouldValidate: true });
    }, []);

    useEffect(() => {
        if (opened) {
            ssiProfileBankInfoApi.mutate();
            setAbortController(new AbortController());
        } else abortController.abort();

        return () => abortController.abort();
    }, [opened]);

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit, onError)} className="relative h-full w-full text-brand-text">
                <FormStepper.Main activeStep={tabIndex} onStep={(step) => setTabIndex(step)}>
                    {steps.map(({ label }, index) => (
                        <FormStepper.Step key={label} step={index} label={label} />
                    ))}
                </FormStepper.Main>
                <Tab.Group defaultIndex={0} selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
                    <Tab.List className="hidden">
                        {steps.map(({ label }) => (
                            <Tab key={label} className="text-sm">
                                {label}
                            </Tab>
                        ))}
                    </Tab.List>
                    <Tab.Panels className="flex-1 basis-0 sm:flex-auto pb-4 w-full h-[500px] overflow-y-auto px-2 sm:px-4">
                        <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                            <fieldset className="space-y-3">
                                <legend className="text-xs text-brand-neutral-500">
                                    {lang.portal.depositInformation}
                                </legend>
                                <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="currencyId"
                                            label={mode === 'bank' ? lang.lite.currency : lang.lite.asset}
                                            asyncInput={!jwt || isCurrenciesLoading}
                                            placeholder={mode === 'bank' ? 'Currency' : 'Asset'}
                                            required>
                                            <FilterSelect
                                                options={fiatDepositCcyOptions}
                                                enableCurrencyIcon
                                                enableSorting
                                            />
                                        </PortalInputController>
                                    </div>
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="amount"
                                            label={lang.portal.amountToDeposit}
                                            placeholder="0.00"
                                            required>
                                            <PortalNumberInput currency={selectedCurrency?.name || ''} />
                                        </PortalInputController>
                                    </div>
                                </div>
                            </fieldset>
                            <fieldset className="space-y-3">
                                <legend className="text-xs text-brand-neutral-500">{lang.portal.bankDetails}</legend>
                                <PortalInputController name="bankInfoId" label={lang.blotters.bankAccount} required>
                                    <Select options={bankOptions} />
                                </PortalInputController>
                                {bankDetails && (
                                    <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                        <div className="flex-1">
                                            <PortalEntry
                                                label={lang.portal.bankName}
                                                value={bankDetails.bankName}
                                                disabled
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <PortalEntry
                                                label={`${lang.portal.accountNumber} / ${lang.portal.ibanCode}`}
                                                value={bankDetails.accountNumber || bankDetails.ibanCode}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                )}
                            </fieldset>
                            <PortalInputController
                                name="requestDescription"
                                label={lang.portal.requestDescription}
                                placeholder={lang.portal.requestDescription}>
                                <TextArea rows={2} />
                            </PortalInputController>
                            <ErrorMessage error={error} />
                            {selectedCurrency?.value && !error.message && (
                                <SSIProfileBankDetails ssiProfile={foundSSIProfile} />
                            )}
                        </Tab.Panel>

                        <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                            {files.length > 0 ? (
                                <div className="flex flex-col gap-2">
                                    <div className="text-xs text-brand-neutral-500">
                                        {lang.portal.supportDetailsFile}
                                    </div>
                                    <div className="flex items-end gap-2">
                                        <span className="text-sm text-brand-text">{files[0].name}</span>
                                        <button
                                            type="button"
                                            className="text-xs text-brand-primary hover:underline hover:text-brand-primary-light"
                                            onClick={() => setFiles([])}>
                                            {lang.common.remove}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <fieldset className="flex flex-col flex-1 basis-0 space-y-3">
                                    <legend className="text-xs text-brand-neutral-500">
                                        {lang.portal.supportingInformationOptional}
                                    </legend>
                                    <div className="h-full min-h-96 flex-1 basis-0">
                                        <Dropzone
                                            name="proofOfOwnership"
                                            className="flex items-center justify-center h-full w-full bg-neutral-background py-2 px-4 rounded-sm border border-dashed border-neutral-500 cursor-pointer"
                                            onDrop={setFiles}
                                            autoFocus={true}
                                            onReject={(error) => {
                                                setError({
                                                    message: DropzoneErrorMessage[error[0].errors[0].code]
                                                });
                                            }}
                                            maxFiles={maxFiles}
                                            maxSize={maxFileSize}
                                            accept={acceptedFileTypes}>
                                            <div className="h-full w-full flex items-center justify-center text-center">
                                                <div className="flex flex-col text-center items-center gap-3 text-sm text-brand-text">
                                                    <Dropzone.Accept>
                                                        <MdOutlineFileUpload className="w-10 h-10" />
                                                    </Dropzone.Accept>
                                                    <Dropzone.Idle>
                                                        <MdOutlineImage className="w-10 h-10" />
                                                    </Dropzone.Idle>
                                                    <Dropzone.Reject>
                                                        <MdClose className="w-10 h-10" />
                                                    </Dropzone.Reject>
                                                    <div className="text-brand-text text-xs">
                                                        {lang.portal.dragOrClickToUpload}
                                                    </div>
                                                    <div className="text-xs text-neutral-500 flex flex-col">
                                                        <span>
                                                            {lang.portal.supportFileFormatsAre}{' '}
                                                            <b>.jpeg, .png, .pdf, .mp4, .avi, .webm, .mpg</b>
                                                        </span>
                                                        <span>
                                                            {lang.portal.attachedFileShouldNotExceed} <b>10mb</b>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Dropzone>
                                    </div>
                                </fieldset>
                            )}
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>
                <Divider neutral />
                <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                    <button
                        type="button"
                        className="rounded-md p-2 px-4 bg-brand-neutral-600 hover:bg-brand-neutral-500"
                        onClick={() => {
                            if (tabIndex === 1) setTabIndex(0);
                            else if (tabIndex === 0) onClose();
                        }}>
                        {tabIndex === 0 ? lang.common.cancel : lang.portal.previousStep}
                    </button>
                    <Button
                        type="button"
                        className="w-auto rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                        onClick={() => {
                            if (tabIndex === 0) setTabIndex(1);
                            else if (!isLoading) handleSubmit(onSubmit, onError)();
                        }}
                        disabled={!isValid && tabIndex === 1}
                        isLoading={isLoading}>
                        {tabIndex === 0 ? lang.portal.nextStep : lang.portal.submitRequest}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default FiatDepositForm;
